import { useEffect, useMemo, useState } from "react";
import { Camera, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SectionHeading } from "@/components/SectionHeading";
import { fetchCollection } from "@/lib/api";
import { formatDate } from "@/utils/format";

const PAGE = 36;

export function PhotoGallery() {
  const [year, setYear] = useState(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setLimit(PAGE), [year, query]);

  const params = useMemo(() => {
    const search = new URLSearchParams({ limit: String(limit) });
    if (year) search.set("year", String(year));
    if (query) search.set("q", query);
    return search.toString();
  }, [year, query, limit]);
  const gallery = useQuery({ queryKey: ["gallery", params], queryFn: () => fetchCollection(`/gallery?${params}`), placeholderData: (previous) => previous });
  const items = gallery.data?.items || [];
  const total = gallery.data?.total || 0;
  const current = selected == null ? null : items[selected];

  return (
    <section id="galeria" className="section-shell" data-testid="gallery-section">
      <SectionHeading number="09" eyebrow="Galería fotográfica" title="Las fotos de trece años de tiradas" description="Todas las fotografías publicadas en TODOtiro desde 2013, ya en la web: premiados, escuadras, campos y podios. Filtra por año o busca un tirador, una localidad o una tirada." />
      <div className="mb-8 grid gap-4 lg:grid-cols-[380px_1fr]" data-testid="gallery-controls">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-clay" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar tirador, localidad o tirada" className="h-12 rounded-none border-white/10 bg-charcoal pl-11 text-slate-100 placeholder:text-slate-500" data-testid="gallery-search-input" />
        </label>
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button type="button" onClick={() => setYear(null)} className={`filter-chip shrink-0 ${year === null ? "filter-chip-active" : ""}`} data-testid="gallery-year-all">Todos</button>
          {(gallery.data?.years || []).map((item) => (
            <button key={item.year} type="button" onClick={() => setYear(Number(item.year))} className={`filter-chip shrink-0 ${year === Number(item.year) ? "filter-chip-active" : ""}`} data-testid={`gallery-year-${item.year}`}>{item.year} <span className="ml-1 text-slate-500">{item.count}</span></button>
          ))}
        </div>
      </div>
      <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500" data-testid="gallery-count">{gallery.isLoading ? "Cargando fotografías..." : `${total.toLocaleString("es-ES")} fotografías`}</p>
      <div className="columns-2 gap-3 sm:columns-3 lg:columns-4 xl:columns-5" data-testid="gallery-grid">
        {items.map((photo, index) => (
          <motion.button key={photo.url} type="button" onClick={() => setSelected(index)} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="group mb-3 block w-full overflow-hidden border border-white/10 text-left" data-testid={`gallery-photo-${index}`}>
            <img src={photo.url} alt={photo.post_title || photo.title} loading="lazy" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" style={photo.width && photo.height ? { aspectRatio: `${photo.width} / ${photo.height}` } : undefined} />
            <span className="block truncate px-2 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500 group-hover:text-clay">{photo.date?.slice(0, 4)} · {photo.location || photo.title}</span>
          </motion.button>
        ))}
      </div>
      {!gallery.isLoading && items.length === 0 ? <p className="border border-dashed border-white/15 p-6 text-slate-400" data-testid="gallery-empty">Ninguna fotografía coincide con la búsqueda.</p> : null}
      {items.length < total ? (
        <div className="mt-8 flex justify-center">
          <Button type="button" onClick={() => setLimit((value) => value + PAGE)} variant="outline" className="h-12 rounded-none border-white/15 bg-transparent px-8 font-mono text-xs uppercase tracking-[0.22em] hover:border-clay/60 hover:bg-clay/10 hover:text-clay" data-testid="gallery-load-more">Ver más fotografías ({(total - items.length).toLocaleString("es-ES")} restantes)</Button>
        </div>
      ) : null}

      <Dialog open={current != null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-6xl rounded-none border-white/10 bg-charcoal p-2 text-slate-100" data-testid="gallery-lightbox">
          {current ? (
            <div className="relative">
              <DialogTitle className="sr-only">{current.post_title || current.title}</DialogTitle>
              <DialogDescription className="sr-only">Fotografía de la galería de TODOtiro</DialogDescription>
              <img src={current.url} alt={current.post_title || current.title} className="max-h-[80vh] w-full object-contain" />
              <button type="button" onClick={() => setSelected((value) => (value - 1 + items.length) % items.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-charcoal/70 p-2 text-clay hover:bg-clay hover:text-charcoal" aria-label="Anterior" data-testid="gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
              <button type="button" onClick={() => setSelected((value) => (value + 1) % items.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-charcoal/70 p-2 text-clay hover:bg-clay hover:text-charcoal" aria-label="Siguiente" data-testid="gallery-next"><ChevronRight className="h-5 w-5" /></button>
              <div className="flex items-center justify-between gap-4 px-3 py-3">
                <div className="min-w-0">
                  <p className="truncate font-display text-2xl uppercase leading-none text-slate-50" data-testid="gallery-lightbox-title">{current.post_title || current.title}</p>
                  <p className="mt-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400"><Camera className="h-3 w-3 text-clay" />{formatDate(current.date)}{current.location ? ` · ${current.location}` : ""}</p>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="p-2 text-slate-400 hover:text-clay" aria-label="Cerrar" data-testid="gallery-close"><X className="h-5 w-5" /></button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
